<template>
  <v-dialog v-model="dialog" max-width="800" persistent scrollable>
    <v-card>
      <v-card-title class="text-h5 teal white--text">
        <v-icon left dark>mdi-medical-bag</v-icon>
        {{ isEdit ? "Edit" : "Add" }} Medical Record
      </v-card-title>

      <v-card-text class="pt-6">
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.visit_date"
                label="Visit Date"
                type="date"
                :rules="[rules.required]"
                outlined
                dense
              >
                <template v-slot:prepend>
                  <v-icon>mdi-calendar</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.visit_time"
                label="Visit Time"
                type="time"
                outlined
                dense
              >
                <template v-slot:prepend>
                  <v-icon>mdi-clock-outline</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.chief_complaint"
                label="Chief Complaint"
                :rules="[rules.required]"
                outlined
                rows="2"
                counter
              ></v-textarea>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.temperature"
                label="Temperature (°C)"
                type="number"
                step="0.1"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.blood_pressure"
                label="Blood Pressure"
                placeholder="120/80"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.pulse"
                label="Pulse (bpm)"
                type="number"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.weight"
                label="Weight (kg)"
                type="number"
                step="0.1"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.symptoms"
                label="Symptoms"
                outlined
                rows="3"
              ></v-textarea>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.diagnosis"
                label="Diagnosis"
                outlined
                rows="3"
              ></v-textarea>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.treatment"
                label="Treatment Plan"
                outlined
                rows="3"
              ></v-textarea>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.prescription"
                label="Prescription"
                outlined
                rows="3"
                hint="Medications prescribed"
              ></v-textarea>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.next_appointment"
                label="Next Appointment"
                type="date"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="formData.follow_up_required"
                :items="[
                  { text: 'Yes', value: true },
                  { text: 'No', value: false },
                ]"
                label="Follow-up Required"
                outlined
                dense
              ></v-select>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.notes"
                label="Additional Notes"
                outlined
                rows="3"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog"> Cancel </v-btn>
        <v-btn color="teal" dark @click="saveMedicalFile" :disabled="!valid">
          <v-icon left>mdi-content-save</v-icon>
          Save Record
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "AddMedicalFileDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    medicalFile: {
      type: Object,
      default: null,
    },
    clientId: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    valid: false,
    formData: {
      visit_date: new Date().toISOString().substr(0, 10),
      visit_time: "",
      chief_complaint: "",
      temperature: "",
      blood_pressure: "",
      pulse: "",
      weight: "",
      symptoms: "",
      diagnosis: "",
      treatment: "",
      prescription: "",
      next_appointment: "",
      follow_up_required: false,
      notes: "",
    },
    rules: {
      required: (v) => !!v || "This field is required",
    },
  }),

  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    isEdit() {
      return this.medicalFile !== null;
    },
  },

  watch: {
    value(val) {
      if (val && this.medicalFile) {
        this.formData = { ...this.medicalFile };
      } else if (val) {
        this.resetForm();
      }
    },
  },

  methods: {
    saveMedicalFile() {
      if (this.$refs.form.validate()) {
        this.$emit("save", {
          ...this.formData,
          client_id: this.clientId,
        });
        this.closeDialog();
      }
    },

    closeDialog() {
      this.$emit("input", false);
      this.$emit("close");
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        visit_date: new Date().toISOString().substr(0, 10),
        visit_time: "",
        chief_complaint: "",
        temperature: "",
        blood_pressure: "",
        pulse: "",
        weight: "",
        symptoms: "",
        diagnosis: "",
        treatment: "",
        prescription: "",
        next_appointment: "",
        follow_up_required: false,
        notes: "",
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>
